import type { GeneratorCommand } from '../janus-engine/inventory/random-generator';
import type { SpawnSourcePackage } from '../janus-engine/spawn/source-plan';
import { withSpawnEntities } from '../janus-engine/spawn/applied-entities';
import type { SpawnRowPlan, Row } from '../janus-engine/spawn/row-plan';
import { spawnAppliedFrame } from '../janus-engine/transport/gm-applied-frame';
import { ecId, record } from '../janus-engine/ability/context';
import { cachedInventoryRows } from '../janus-engine/inventory/cached-inventory-trees';
import { readInventoryField } from '../janus-engine/compile/inventory-raw-readers';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { surfaceField } from '../janus-engine/compile/from-base-encounter-media';
import { encounterField, encounterCreatureField } from './array-types';
import { normalizePhpListish } from './php-listish';
import { ftClockCampaignField } from './campaign-clock';
import { ftCurrentTruthSink, truthWriteRefusal } from './truth-sink';
import type {} from './applied-truth-transport';

export const GENERATOR_SAVE_PATH = 'options/tools/inventory/random_generator/generator_truth_commit_inc.php';

/** Each caller retains this one birth with its retry command. No bundle-owned state. */
export function prepareGeneratorDelivery(command: GeneratorCommand, inputs: SpawnSourcePackage, encounter: Row): (response: Response) => Promise<void> {
    if (ftCurrentTruthSink()) throw truthWriteRefusal(BASE_URL + GENERATOR_SAVE_PATH);
    if (!window.ftPushAppliedTruth) throw new Error('generator:truth-transport-unavailable');
    const tables = { inventory: 'inventory', status: 'statuses', template: 'enchantment_templates', member: 'enchantment_template_enchantments' } as const;
    const first = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr.find(value => ecId(record(value))) : null;
    const identity = { operation_id: command.operation_id, encounter_id: command.destination_id, actor_id: ecId(record(first)) || command.destination_id, rev: 0, member_revs: {} };
    const creator = String((window as unknown as { FateTide?: { user?: { id?: unknown } } }).FateTide?.user?.id ?? '');
    const held = record(record(encounter._entity_base)?.base) ?? encounter;
    const campaign = record(encounter.campaign_arr) ?? record(held.campaign_arr) ?? record(record(held.encounter_context)?.campaign_arr);
    const campaignId = ftClockCampaignField(campaign, 'campaign_id')
        ?? (encounterField(encounter, 'encounter_master_parent_type') === 'campaign' ? encounterField(encounter, 'encounter_master_parent_id') : null);
    const privacy = new Map([[command.destination_id.toUpperCase(), Number(encounterField(encounter, 'encounter_private')
        ?? encounterField(held, 'encounter_private') ?? surfaceField(record(encounter.locations) ?? record(held.locations) ?? {}, 'location_private') ?? 0)]]);
    for (const host of held === encounter ? [held] : [held, encounter]) {
        for (const node of cachedInventoryRows(host)) privacy.set(String(readInventoryField(node, 'inventory_id')).toUpperCase(),
            Number(readInventoryField(node, 'inventory_private') ?? 0));
        for (const member of normalizePhpListish(host.encounter_creatures_arr)) privacy.set(ecId(record(member)),
            Number(encounterCreatureField(member, 'encounter_creature_private') ?? 0));
    }
    const newborns = new Map(command.rows.filter(op => op.kind === 'inventory')
        .map(({ row }) => [String(readInventoryField({ inventory: row }, 'inventory_id')).toUpperCase(), row]));
    const parentPrivacy = (id: string): number => {
        const key = id.toUpperCase(), known = privacy.get(key);
        if (known !== undefined) return known;
        const row = newborns.get(key);
        if (!row) throw new Error(`generator:birth-parent-unavailable:${id}`);
        const value = parentPrivacy(String(readInventoryField({ inventory: row }, 'possess_id')));
        privacy.set(key, value); return value;
    };
    // Match the save's parent-derived columns for compilation/publication only.
    // The POST keeps its original allowlist and never carries these ownership fields.
    const plan: SpawnRowPlan = { version: 'janus.spawn.rows.v1', writes: command.rows.map(({ kind, row }) => ({ table: tables[kind], op: 'insert',
        row: kind === 'inventory' ? { ...row, inventory_private: parentPrivacy(String(readInventoryField({ inventory: row }, 'possess_id'))), inventory_deleted: 0 }
            : kind === 'status' ? { ...row, status_private: parentPrivacy(String(readItemField(row, 'status_caster_id'))),
                status_campaign_id: campaignId, status_deleted: 0, status_created_by_id: creator } : row })) };
    const applied_frame = spawnAppliedFrame(identity, withSpawnEntities(plan, inputs, encounter, creator));
    let published = false;
    return async response => {
        if (published) return;
        published = true;
        // Publish after success/replay, then register the existing response. Claiming
        // the save first would dedup away this operation's first frame.
        const installed = window.ftPushAppliedTruth!({ type: 'janus_spawn_truth', command: identity, applied_frame });
        await window.ftSaveAppliedTruth?.(command.operation_id, async () => response);
        await installed;
    };
}
